"use client";

import { useEffect, useRef, useState } from "react";
import Link from "@/components/i18n/link";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/lib/store/auth-store";
import { useInterestsStore } from "@/lib/store/interests-store";
import { useInterestCountsStore } from "@/lib/store/interest-counts-store";
import { toggleEventInterest } from "@/lib/api";
import type { CiudadVivaEvent } from "@/lib/events";
import { recordInterestClick } from "@/lib/newsletter-soft-ask";
import { triggerNewsletterSoftAsk } from "@/components/NewsletterSoftAsk";
import { useMessages } from "@/lib/i18n/client";
import messages from "./InterestButton.i18n";
import { track } from "@/lib/tracking/track";

interface Props {
  event: CiudadVivaEvent;
  variant?: "compact" | "full";
}

export function InterestButton({ event, variant = "compact" }: Props) {
  const user = useAuthStore((s) => s.user);
  const interested = useInterestsStore((s) => s.slugs.has(event.slug));
  const setInterested = useInterestsStore((s) => s.toggle);
  const count = useInterestCountsStore((s) => s.counts[event.slug] ?? event.interestCount ?? 0);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const t = useMessages(messages);

  // Se registra solo mientras está REALMENTE visible en pantalla (no solo
  // montado) — en /eventos el botón "Cargar más" agrega cards sin desmontar
  // las anteriores, así que con miles de eventos cargados el set de
  // "montados" crecería sin límite y el poller terminaría pidiendo miles de
  // conteos cada 20s. Con IntersectionObserver el tamaño del pedido queda
  // acotado a lo que cabe en el viewport (+ margen), sin importar cuánto se
  // haya scrolleado/cargado.
  useEffect(() => {
    useInterestCountsStore.getState().seed(event.slug, event.interestCount ?? 0);

    const el = buttonRef.current;
    if (!el) return;

    let isRegistered = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const { register, unregister } = useInterestCountsStore.getState();
        if (entry.isIntersecting && !isRegistered) {
          register(event.slug);
          isRegistered = true;
        } else if (!entry.isIntersecting && isRegistered) {
          unregister(event.slug);
          isRegistered = false;
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (isRegistered) useInterestCountsStore.getState().unregister(event.slug);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.slug]);

  // Pulso breve cuando el conteo sube o baja — propio (toggle) o ajeno (polling).
  const prevCountRef = useRef(count);
  const [pulse, setPulse] = useState<"up" | "down" | null>(null);
  useEffect(() => {
    if (count === prevCountRef.current) return;
    setPulse(count > prevCountRef.current ? "up" : "down");
    prevCountRef.current = count;
    const timer = setTimeout(() => setPulse(null), 500);
    return () => clearTimeout(timer);
  }, [count]);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      setLoginPromptOpen(true);
      track("login_prompt_shown", { trigger: "save_event" });
      return;
    }

    const { setCount } = useInterestCountsStore.getState();
    const prevInterested = interested;
    const prevCount = count;
    setInterested(event.slug, !prevInterested);
    setCount(event.slug, prevInterested ? prevCount - 1 : prevCount + 1);

    try {
      const result = await toggleEventInterest(event.slug);
      setInterested(event.slug, result.interested);
      setCount(event.slug, result.count);
      track(result.interested ? "event_save" : "event_unsave", { slug: event.slug });

      if (result.interested === true) {
        const interestCount = recordInterestClick();
        if (interestCount === 2) triggerNewsletterSoftAsk();
      }
    } catch {
      setInterested(event.slug, prevInterested);
      setCount(event.slug, prevCount);
      toast.error(t.updateError);
    }
  };

  const icon = (
    <Heart
      className={`h-4 w-4 transition-transform duration-300 ${pulse ? "scale-125" : "scale-100"} ${
        interested ? "fill-primary text-primary" : ""
      }`}
    />
  );

  return (
    <>
      {variant === "compact" ? (
        <button
          ref={buttonRef}
          onClick={handleClick}
          aria-label={t.interested}
          className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-background/95 px-2.5 py-1 text-xs font-medium text-foreground backdrop-blur transition hover:bg-background"
        >
          {icon}
        </button>
      ) : (
        <button
          ref={buttonRef}
          onClick={handleClick}
          className={`inline-flex cursor-pointer items-center gap-2 transition-colors ${
            interested ? "text-primary" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {icon}
          <span className="text-sm">{t.interested}</span>
        </button>
      )}

      <Dialog open={loginPromptOpen} onOpenChange={setLoginPromptOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.loginTitle}</DialogTitle>
            <DialogDescription className="mt-1.5">
              {t.loginDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3">
            <Link
              href="/registro"
              onClick={() => setLoginPromptOpen(false)}
              className="inline-flex h-11 flex-1 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              {t.createAccount}
            </Link>
            <Link
              href="/login"
              onClick={() => setLoginPromptOpen(false)}
              className="inline-flex h-11 flex-1 items-center justify-center rounded-full border border-border text-sm font-medium transition hover:border-foreground/40"
            >
              {t.login}
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
